/**
 * TitleLabel component module.
 * @module shared/components/TitleLabel
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {connect} from 'react-redux';
import Icon from './Icon';
import {_openModal} from '../reducers/modals';
import {getBlackWhiteWithOpacity} from '../utils/colors';

const _TitleLabel = styled.span`
  display: flex;
  align-items: center;
  text-align: left;
  margin: ${props => props.margin};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  font-family: ${props => props.theme.labelText.name};
  font-size: ${props => props.theme.labelText.size};
  
  ${props => props.clickable ? 'cursor: pointer;' : 'cursor: default;'}

  ${props => !props.disableHoverHighlight && props.spansEntireLabel && `
  :hover {
    border-radius: 3px;
    background-color: ${getBlackWhiteWithOpacity(0.1)};
  };
  transition: background-color 0.5s;
  `};
`;

const _InnerLabel = styled.span`
  display: flex;
  align-items: center;
  text-align: left;
`;

/**
 * TitleLabel component
 * @name TitleLabel
 */
const TitleLabel = props => {
  const {
    title,
    actionIcon,
    help,
    clickable,
    margin,
    disableHoverHighlight,
    openModal,
  } = props;
  const type = help?.type || 'modal';

  return (
    <_TitleLabel margin={margin}
                 clickable={clickable}
                 disableHoverHighlight={disableHoverHighlight}
                 spansEntireLabel={actionIcon?.spansEntireLabel}
                 onClick={actionIcon?.spansEntireLabel ? actionIcon?.action : null}
                 ref={actionIcon?.actionPopoverRef}>
      <_InnerLabel>
        {title}:
        {actionIcon && <Icon
          height='14px'
          width='14px'
          margin='0 5px 0 5px'
          description={actionIcon?.description}
          svg={actionIcon?.icon}
          action={!actionIcon?.spansEntireLabel ? actionIcon?.action : null}
          isPopoverControlled={actionIcon?.spansEntireLabel}
          showPopover={actionIcon?.showActionPopover}
        />}
      </_InnerLabel>
      {help && (
        <Icon
          height='12px'
          width='12px'
          margin='0 0 0 2px'
          description={(help.type === 'tooltip') ? help.description : null}
          svg={'help'}
          action={['action', 'modal'].includes(type) ? (e => {
            e?.stopPropagation();

            if (type === 'action') {
              help.action();
            } else if (type === 'modal') {
              openModal({
                type: 'MessageModal',
                size: help.size || 'sm',
                body: help.content,
                title: help.title,
                button: help.button,
              });
            } else {
              console.warn(`TitleLabel: invalid type "${type}"`);
            }
          }) : null}
        />
      )}
    </_TitleLabel>
  );
};

const mapDispatchToProps = dispatch => ({
  openModal: content => dispatch(_openModal(content)),
});

export default connect(null, mapDispatchToProps)(TitleLabel);

TitleLabel.propTypes = {
  /**
  * Label title
  */
  title: PropTypes.string,
  /**
   * If passed in, then an action icon is displayed
   */
  actionIcon: PropTypes.shape({
    /**
     * The icon svg
     */
    icon: PropTypes.string.isRequired,
    /**
     * The action to perform when icon is clicked
     */
    action: PropTypes.func.isRequired,
    /**
     * The popover message/description
     */
    description: PropTypes.string,
    /**
     * Whether the actionIcon behavior spans across the entire label (i.e., click and hover)
     */
    spansEntireLabel: PropTypes.bool,
    /**
     * Whether to show popover for actionIcon
     */
    showActionPopover: PropTypes.bool,
    /**
     * Reference passed from parent
     */
    actionPopoverRef: PropTypes.node,
  }),
  /**
   * If passed in, then help icon displays per content, description, title, and type properties
   */
  help: PropTypes.shape({
    /**
     * Help type
     */
    type: PropTypes.oneOf(['action', 'modal', 'tooltip']).isRequired,
    /**
     * Help icon description
     */
    description: PropTypes.string,
    /**
     * If help type is modal, the modal size. Defaults to sm
     */
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    /**
     * If help type is modal, the modal title
     */
    title: PropTypes.string,
    /**
     * If help type is modal, the modal body
     */
    content: PropTypes.node,
    /**
     * If help type is action, the action to perform
     */
    action: PropTypes.func,
    /**
     * If button needs to be customized, then pass the text, action, and data for button
     */
    button: PropTypes.shape({
      text: PropTypes.string,
      action: PropTypes.func,
      data: PropTypes.object,
    }),
  }),
  /**
   * Whether parent has an action triggered on click (controls pointer display)
   */
  clickable: PropTypes.bool,
  /**
   * Optional margin
   */
  margin: PropTypes.string,
  /**
   * Disable hover highlight
   */
  disableHoverHighlight: PropTypes.bool,
  /**   * Opens modal on page
   */
  openModal: PropTypes.func.isRequired,
};
